import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Premium empty state — icon + friendly title + one-line hint + optional
 * primary CTA + optional "Explore Demo" secondary CTA.
 *
 *   <EmptyState
 *     icon={CreditCard}
 *     title="Your cards, all in one place."
 *     hint="Add your first card to track spending, payments and dates."
 *     cta={{ to: "/cards/new", label: "Add a card" }}
 *     demoTo="/cards"
 *   />
 *
 * `demoTo` (optional) — when set, renders an "Explore Demo" button that
 * flips the caller into the shared read-only Global Demo workspace and
 * navigates to that path so they can see what a populated module looks
 * like without adding any real data.
 */
function EmptyState({ icon: Icon, title, hint, cta, demoTo, tone = "neutral", className = "", "data-testid": testId }) {
    const toneCls = {
        neutral:  "bg-stone-50 border-stone-200 text-stone-500",
        positive: "bg-emerald-50/70 border-emerald-200 text-emerald-700",
        info:     "bg-blue-50/70 border-blue-200 text-blue-700",
        warning:  "bg-amber-50/70 border-amber-200 text-amber-800",
    }[tone] || "bg-stone-50 border-stone-200 text-stone-500";

    const exploreDemo = async () => {
        try {
            await api.post("/workspace/switch");
            window.location.href = demoTo || "/dashboard";
        } catch { /* silent — user stays put */ }
    };

    return (
        <div
            data-testid={testId || "empty-state"}
            className={`w-full rounded-2xl border border-dashed ${toneCls} py-10 px-6 flex flex-col items-center text-center gap-3 ${className}`}
        >
            {Icon && (
                <div className="w-11 h-11 rounded-full bg-white/70 border border-stone-200 flex items-center justify-center shadow-sm">
                    <Icon size={18} strokeWidth={1.6} />
                </div>
            )}
            <div>
                <div className="text-[15px] font-semibold text-stone-900">{title}</div>
                {hint && <div className="text-[12.5px] text-stone-500 mt-1 max-w-md">{hint}</div>}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                {cta && (
                    <Link to={cta.to} data-testid={cta.testId || "empty-state-cta"}>
                        <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white">
                            {cta.label} <ArrowRight size={12} className="ml-1.5" />
                        </Button>
                    </Link>
                )}
                {demoTo && (
                    <button
                        onClick={exploreDemo}
                        data-testid="empty-state-demo-cta"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[12.5px] font-semibold hover:bg-amber-100 transition-colors"
                    >
                        <Sparkles size={12} /> Explore Demo
                    </button>
                )}
            </div>
        </div>
    );
}

/** Table-row variant. Use inside <tbody> when the table is empty. */
function EmptyRow({ colSpan, icon: Icon, title, hint, cta, demoTo, tone = "neutral" }) {
    return (
        <tr>
            <td colSpan={colSpan} className="p-0">
                <EmptyState icon={Icon} title={title} hint={hint} cta={cta} demoTo={demoTo} tone={tone} className="rounded-none border-0 py-12" />
            </td>
        </tr>
    );
}

EmptyState.Row = EmptyRow;
export default EmptyState;
